import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FilterableSelect } from '../common/FilterableSelect';
import type { QueueTrainingRequest } from '../../api/queries/lora';

interface TrainLoraDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (request: QueueTrainingRequest) => void;
  entityName: string;
  imageCount: number;
  descriptorCount: number;
  isSubmitting: boolean;
}

const TRAINER_OPTIONS = [
  { value: 'simpletuner', label: 'SimpleTuner (recommended)' },
  { value: 'kohya', label: 'Kohya (large datasets)' },
];

export function TrainLoraDialog({
  open,
  onClose,
  onSubmit,
  entityName,
  imageCount,
  descriptorCount,
  isSubmitting,
}: TrainLoraDialogProps) {
  const [trainer, setTrainer] = useState('simpletuner');
  const [rank, setRank] = useState(16);
  const [steps, setSteps] = useState(1500);
  const [learningRate, setLearningRate] = useState(0.0001);

  const handleSubmit = () => {
    onSubmit({
      trainer,
      hyperparameterOverrides: {
        rank,
        steps,
        learning_rate: learningRate,
      },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Train LoRA Model</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Train a LoRA adapter for <strong>{entityName}</strong>
            {imageCount > 0 && (
              <>
                {' '}
                using {imageCount} reference image{imageCount !== 1 ? 's' : ''}
              </>
            )}
            {imageCount === 0 && descriptorCount > 0 && (
              <>
                {' '}
                using {descriptorCount} descriptor
                {descriptorCount !== 1 ? 's' : ''} (images will be generated)
              </>
            )}
            .
          </Typography>

          {imageCount > 0 && imageCount < 5 && (
            <Typography variant="body2" color="warning.main">
              Fewer than 5 reference images may produce lower quality results.
              Consider adding more images before training.
            </Typography>
          )}

          <FilterableSelect
            options={TRAINER_OPTIONS}
            value={trainer}
            onChange={setTrainer}
            label="Trainer"
            allowEmpty={false}
          />

          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            Hyperparameters (advanced)
          </Typography>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Rank"
              type="number"
              size="small"
              value={rank}
              onChange={(e) => setRank(Number(e.target.value))}
              slotProps={{
                htmlInput: { min: 4, max: 128, step: 4 },
              }}
              helperText="4-128, higher = more capacity"
              fullWidth
            />
            <TextField
              label="Steps"
              type="number"
              size="small"
              value={steps}
              onChange={(e) => setSteps(Number(e.target.value))}
              slotProps={{
                htmlInput: { min: 500, max: 5000, step: 100 },
              }}
              helperText="500-5000"
              fullWidth
            />
          </Stack>

          <TextField
            label="Learning Rate"
            type="number"
            size="small"
            value={learningRate}
            onChange={(e) => setLearningRate(Number(e.target.value))}
            slotProps={{
              htmlInput: {
                min: 0.00001,
                max: 0.001,
                step: 0.00001,
              },
            }}
            helperText="Default: 0.0001"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting || (imageCount === 0 && descriptorCount === 0)}
        >
          {isSubmitting ? 'Queuing...' : 'Start Training'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
